import React, { useState, useEffect } from "react";
import {
  FaEye, FaBuilding, FaMapMarkerAlt, FaTimes, FaFileUpload,
  FaTrash, FaDownload, FaFile, FaPlus, FaEdit, FaExclamationTriangle,
  FaSearch, FaUserCheck, FaUserClock, FaCalendar, FaIdCard
} from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import { useUserBrands, useCreateBrand, useUpdateBrand, useDeleteBrand } from "../../hooks/useBrands.ts";
import { getLocationByPinCode } from "../../services/Common.ts";
import { useProfile } from "../../hooks/useProfile.ts";
import { useTheme } from "../../contexts/ThemeContext.tsx";
import { showConfirmation } from "../../services/confirmation.ts";
import { themes } from "../../theme.ts";

interface Brand {
  id: number;
  title: string;
  description: string;
  logo: string;
  location: string;
  district?: string;
  state?: string;
  pincode?: string;
  created_at: string;
  documents?: { name: string; url: string }[];
}

interface DocumentUpload {
  name: string;
  file: File;
}

interface UploadProgress {
  fileName: string;
  progress: number;
}

interface BrandDocument {
  id: number;
  name: string;
  file: string;
  uploaded_at: string;
}

const Brands = () => {
  const { themeName } = useTheme();
  const currentTheme = themes[themeName]?.colors;

  const rawData = localStorage.getItem('user');
  const cred = rawData ? JSON.parse(rawData) : null;
  const token = cred?.access;

  const [brands, setBrands] = useState<Brand[]>([]);
  const [newBrand, setNewBrand] = useState<{
    title: string;
    description: string;
    logo: File | null;
    location: string;
    district: string;
    state: string;
    pincode: string
  }>({
    title: "",
    description: "",
    logo: null,
    location: "",
    district: "",
    state: "",
    pincode: ""
  });

  // UPDATE BRAND STATES
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingBrand, setEditingBrand] = useState<Brand | null>(null);
  const [editForm, setEditForm] = useState<{
    id: number;
    title: string;
    description: string;
    logo: File | string | null;
    location: string;
    district: string;
    state: string;
    pincode: string;
  }>({
    id: 0,
    title: "",
    description: "",
    logo: null,
    location: "",
    district: "",
    state: "",
    pincode: ""
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [viewBrand, setViewBrand] = useState<Brand | null>(null);
  const [loading, setLoading] = useState(false);
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);
  const [documents, setDocuments] = useState<DocumentUpload[]>([]);
  const [documentName, setDocumentName] = useState("");
  const [uploadProgress, setUploadProgress] = useState<UploadProgress[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isViewDocumentsModalOpen, setIsViewDocumentsModalOpen] = useState(false);
  const [brandDocuments, setBrandDocuments] = useState<BrandDocument[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [isPinValid, setIsPinValid] = useState(true);
  const [isEditPinValid, setIsEditPinValid] = useState(true);

  // DELETE CONFIRMATION STATES
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [brandToDelete, setBrandToDelete] = useState<Brand | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Search functionality
  const [searchTerm, setSearchTerm] = useState('');

  const { data: brandsData, isLoading: isLoadingBrands, refetch: refetchBrands } = useUserBrands(cred?.user?.id);
  const createBrandMutation = useCreateBrand();
  const updateBrandMutation = useUpdateBrand();
  const deleteBrandMutation = useDeleteBrand();

  useEffect(() => {
    if (brandsData) {
      setBrands(brandsData);
    }
  }, [brandsData]);

  //-----------
  const { data: profileData, isLoading: isLoadingProfile } = useProfile(cred?.user?.id);
  const [isProfileVerified, setIsProfileVerified] = useState(false);
  const [profileCheckComplete, setProfileCheckComplete] = useState(false);


  // Fetch location details for new brand form
  const fetchLocationDetails = async (pincode: string) => {
    try {
      const location = await getLocationByPinCode(pincode);
      if (location?.status === "Success") {
        setIsPinValid(true);
        setNewBrand(prev => ({
          ...prev,
          location: location.city || "",
          district: location.district || "",
          state: location.state || ""
        }));
      } else {
        setIsPinValid(false);
        setNewBrand(prev => ({
          ...prev,
          location: "",
          district: "",
          state: ""
        }));
      }
    } catch (error) {
      console.error("Location fetch error:", error);
      setIsPinValid(false);
    }
  };

  // Fetch location details for edit brand form
  const fetchEditLocationDetails = async (pincode: string) => {
    try {
      const location = await getLocationByPinCode(pincode);
      if (location?.status === "Success") {
        setIsEditPinValid(true);
        setEditForm(prev => ({
          ...prev,
          location: location.city || "",
          district: location.district || "",
          state: location.state || ""
        }));
      } else {
        setIsEditPinValid(false);
        setEditForm(prev => ({
          ...prev,
          location: "",
          district: "",
          state: ""
        }));
      }
    } catch (error) {
      console.error("Location fetch error:", error);
      setIsEditPinValid(false);
    }
  };

  useEffect(() => {
    if (newBrand.pincode.length === 6) {
      fetchLocationDetails(newBrand.pincode);
    } else {
      setIsPinValid(false);
    }
  }, [newBrand.pincode]);

  useEffect(() => {
    if (editForm.pincode.length === 6) {
      fetchEditLocationDetails(editForm.pincode);
    } else {
      setIsEditPinValid(false);
    }
  }, [editForm.pincode]);

  // Handle input changes for new brand form
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === 'pincode') {
      setNewBrand(prev => ({ ...prev, [name]: value }));
      if (value.length === 6) {
        fetchLocationDetails(value);
      } else {
        setIsPinValid(false);
        setNewBrand(prev => ({
          ...prev,
          location: "",
          district: "",
          state: ""
        }));
      }
    } else {
      setNewBrand(prev => ({ ...prev, [name]: value }));
    }
  };

  // Handle input changes for edit brand form
  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === 'pincode') {
      setEditForm(prev => ({ ...prev, [name]: value }));
      if (value.length === 6) {
        fetchEditLocationDetails(value);
      } else {
        setIsEditPinValid(false);
        setEditForm(prev => ({
          ...prev,
          location: "",
          district: "",
          state: ""
        }));
      }
    } else {
      setEditForm(prev => ({ ...prev, [name]: value }));
    }
  };

  // Handle image upload for new brand form
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setNewBrand({ ...newBrand, logo: file });
    }
  };

  // Handle image upload for edit brand form
  const handleEditImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setEditForm({ ...editForm, logo: file });
    }
  };

  // Add new brand
  const handleAddBrand = async () => {
    if (newBrand.title && newBrand.description && newBrand.location && newBrand.logo) {
      setIsUploading(true);
      const formData = new FormData();
      formData.append('title', newBrand.title);
      formData.append('description', newBrand.description);
      if (newBrand.logo) formData.append('logo', newBrand.logo);
      formData.append('location', newBrand.location);
      formData.append('district', newBrand.district);
      formData.append('state', newBrand.state);
      formData.append('pincode', newBrand.pincode);

      try {
        await createBrandMutation.mutateAsync(formData);
        setIsModalOpen(false);
        setNewBrand({
          title: "",
          description: "",
          logo: null,
          location: "",
          district: "",
          state: "",
          pincode: ""
        });
      } catch (err) {
        console.log(err);
      } finally {
        setIsUploading(false);
      }
    }
  };

  // UPDATE BRAND FUNCTION
  const handleUpdateBrand = async () => {
    if (editForm.title && editForm.description && editForm.location) {
      setIsUploading(true);
      const formData = new FormData();
      formData.append('title', editForm.title);
      formData.append('description', editForm.description);
      if (editForm.logo instanceof File) {
        formData.append('logo', editForm.logo);
      }
      formData.append('location', editForm.location);
      formData.append('district', editForm.district);
      formData.append('state', editForm.state);
      formData.append('pincode', editForm.pincode);

      try {
        await updateBrandMutation.mutateAsync({ id: editForm.id.toString(), data: formData });
        setIsEditModalOpen(false);
        setEditForm({
          id: 0,
          title: "",
          description: "",
          logo: null,
          location: "",
          district: "",
          state: "",
          pincode: ""
        });
      } catch (err) {
        console.log(err);
      } finally {
        setIsUploading(false);
      }
    }
  };

  // View brand details
  const handleViewBrand = (brand: Brand) => {
    setViewBrand(brand);
  };

  // Add function to fetch brand documents
  const fetchBrandDocuments = async (brandId: number) => {
    setLoadingDocuments(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_DB_URI}/brand-documents/?brand_id=${brandId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setBrandDocuments(data);
      } else {
        console.error("Failed to fetch documents");
      }
    } catch (error) {
      console.error("Error fetching documents:", error);
    } finally {
      setLoadingDocuments(false);
    }
  };

  // Setup edit form when opening edit modal
  const handleEditBrand = (brand: Brand) => {
    setEditingBrand(brand);
    setEditForm({
      id: brand.id,
      title: brand.title,
      description: brand.description,
      logo: brand.logo, // This is a URL string initially
      location: brand.location,
      district: brand.district || "",
      state: brand.state || "",
      pincode: brand.pincode || ""
    });
    setIsEditModalOpen(true);
  };

  const getAllBrands = async () => {
    // Replaced by useUserBrands hook
    refetchBrands();
  };

  // Open delete confirmation modal
  const handleDeleteClick = (brand: Brand) => {
    setBrandToDelete(brand);
    setIsDeleteModalOpen(true);
  };

  // Confirm and execute delete
  const handleConfirmDelete = async () => {
    if (!brandToDelete) return;

    setIsDeleting(true);
    try {
      await deleteBrandMutation.mutateAsync(brandToDelete.id.toString());
      setBrands(brands.filter((brand) => brand.id !== brandToDelete.id));
      setIsDeleteModalOpen(false);
      setBrandToDelete(null);
    } catch (err) {
      console.log(err);
    } finally {
      setIsDeleting(false);
    }
  };

  // Cancel delete
  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setBrandToDelete(null);
  };

  const handleDocumentUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0] && documentName.trim()) {
      const file = e.target.files[0];
      setDocuments([...documents, { name: documentName, file }]);
      setDocumentName("");
      // Reset file input
      e.target.value = "";
    }
  };

  // Update handleDocumentSubmission to include progress tracking
  const handleDocumentSubmission = async () => {
    if (!viewBrand?.id || documents.length === 0) return;

    setIsUploading(true);
    setUploadProgress(documents.map(doc => ({ fileName: doc.name, progress: 0 })));

    const formData = new FormData();
    formData.append("brand_id", viewBrand?.id.toString());

    documents.forEach((doc, index) => {
      formData.append("documents", doc.file);
      formData.append("names", doc.name);
    });

    // Log FormData for debugging
    for (let pair of formData.entries()) {
      console.log(pair[0], pair[1]);
    }

    try {
      const xhr = new XMLHttpRequest();

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(prev =>
            prev.map(item => ({ ...item, progress }))
          );
        }
      };

      xhr.onload = () => {
        if (xhr.status === 200 || xhr.status === 201) {
          console.log("Upload successful:", xhr.response);
          setIsDocumentModalOpen(false);
          setDocuments([]);
          setUploadProgress([]);
          // TODO: Add success notification
        } else {
          console.error("Upload failed:", xhr.response);
          // TODO: Add error notification
        }
        setIsUploading(false);
      };

      xhr.onerror = () => {
        console.error("Upload failed:", xhr.response);
        setIsUploading(false);
        // TODO: Add error notification
      };

      xhr.open("POST", `${process.env.REACT_APP_DB_URI}/brand-documents/`);
      xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      // Remove Content-Type header to let browser set it with boundary
      xhr.send(formData);

    } catch (error) {
      console.error("Error uploading documents:", error);
      setIsUploading(false);
      setUploadProgress([]);
      // TODO: Add error notification
    }
  };

  // Add this function to remove document
  const removeDocument = (index: number) => {
    setDocuments(documents.filter((_, i) => i !== index));
  };

  //----------------------------
  useEffect(() => {
    if (profileData) {
      const isVerified = (profileData as any)?.user?.profile?.is_verified || false;
      setIsProfileVerified(isVerified);
      setProfileCheckComplete(true);
    } else if (!isLoadingProfile) {
      setProfileCheckComplete(true);
    }
  }, [profileData, isLoadingProfile]);

  return (
    <div className="min-h-screen py-8" style={{ backgroundColor: currentTheme.background }}>
      {/* Header Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2" style={{ color: currentTheme.text }}>
            Brand Management
          </h1>
          <p className="text-lg" style={{ color: currentTheme.textMuted }}>
            Manage your business brands and associated documents
          </p>
        </div>

        {/* Add Brand Button & Search */}
        {profileCheckComplete && (
          <>
            {isProfileVerified ? (
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 mb-8 p-6 rounded-2xl shadow-lg border"
                style={{ backgroundColor: currentTheme.card, borderColor: currentTheme.border }}>
                <div className="flex-1 max-w-2xl">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaSearch className="h-5 w-5" style={{ color: currentTheme.textMuted }} />
                    </div>
                    <input
                      type="text"
                      placeholder="Search brands by name, location, or state..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="block w-full pl-10 pr-3 py-3 border rounded-2xl text-sm focus:outline-none focus:ring-2 transition-all duration-200"
                      style={{
                        backgroundColor: currentTheme.surface,
                        color: currentTheme.text,
                        borderColor: currentTheme.border,
                        focusBorderColor: currentTheme.primary
                      }}
                    />
                  </div>
                </div>

                <button
                  onClick={() => setIsModalOpen(true)}
                  className="flex items-center justify-center gap-3 px-6 py-3 rounded-2xl font-semibold text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                  style={{ backgroundColor: currentTheme.primary }}
                >
                  <FaPlus className="h-5 w-5" />
                  Add New Brand
                </button>
              </div>
            ) : (
              <div className="p-6 rounded-2xl border mb-8 flex items-center gap-4"
                style={{ backgroundColor: currentTheme.card, borderColor: currentTheme.warning, color: currentTheme.warning }}>
                <div className="flex-shrink-0">
                  <FaUserClock className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Profile Verification Required</h3>
                  <p className="text-sm opacity-90">Complete your profile verification to create and manage brands</p>
                </div>
              </div>
            )}
          </>
        )}

        {/* Brands Grid */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="relative">
              <div className="w-16 h-16 border-4 rounded-full animate-spin"
                style={{ borderTopColor: currentTheme.primary, borderRightColor: 'transparent', borderBottomColor: currentTheme.primary, borderLeftColor: 'transparent' }}></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <FaBuilding style={{ color: currentTheme.primary }} className="h-6 w-6" />
              </div>
            </div>
            <p className="mt-4 text-lg font-medium" style={{ color: currentTheme.primary }}>Loading your brands...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
            {brands.filter((brand) => {
              const keyword = searchTerm.toLowerCase();
              return (
                brand?.title?.toLowerCase().includes(keyword) ||
                brand?.location?.toLowerCase().includes(keyword) ||
                brand?.state?.toLowerCase().includes(keyword)
              );
            }).length > 0 ? (
              brands
                .filter((brand) => {
                  const keyword = searchTerm.toLowerCase();
                  return (
                    brand?.title?.toLowerCase().includes(keyword) ||
                    brand?.location?.toLowerCase().includes(keyword) ||
                    brand?.state?.toLowerCase().includes(keyword)
                  );
                })
                .map((brand, index) => (
                  <div
                    key={index}
                    className="rounded-2xl shadow-lg hover:shadow-xl border transform hover:scale-105 transition-all duration-300 overflow-hidden group"
                    style={{ backgroundColor: currentTheme.card, borderColor: currentTheme.border }}
                  >
                    {/* Brand Image */}
                    <div className="h-48 overflow-hidden relative">
                      <img
                        src={brand?.logo}
                        alt="Brand Logo"
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-300"></div>
                    </div>

                    {/* Brand Content */}
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="text-xl font-bold truncate flex-1 mr-3" style={{ color: currentTheme.primary }}>
                          {brand?.title}
                        </h3>
                        <span className="flex-shrink-0 px-2 py-1 text-xs rounded-full font-medium"
                          style={{ backgroundColor: currentTheme.primary + '20', color: currentTheme.primary }}>
                          Active
                        </span>
                      </div>

                      <div className="space-y-2 mb-4">
                        <div className="flex items-center gap-2 text-sm">
                          <FaMapMarkerAlt style={{ color: currentTheme.primary }} className="h-4 w-4 flex-shrink-0" />
                          <span style={{ color: currentTheme.textMuted }} className="truncate">{brand?.location}</span>
                        </div>
                        {brand?.state && (
                          <div className="flex items-center gap-2 text-sm">
                            <FaIdCard style={{ color: currentTheme.primary }} className="h-3 w-3 flex-shrink-0" />
                            <span style={{ color: currentTheme.textMuted }}>{brand.state}</span>
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center justify-between pt-4 border-t" style={{ borderColor: currentTheme.border }}>
                        <button
                          onClick={() => handleViewBrand(brand)}
                          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 hover:shadow-md"
                          style={{
                            backgroundColor: currentTheme.surface,
                            color: currentTheme.primary,
                            border: `1px solid ${currentTheme.border}`
                          }}
                        >
                          <FaEye className="h-4 w-4" />
                          View
                        </button>

                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleEditBrand(brand)}
                            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 hover:shadow-md"
                            style={{
                              backgroundColor: currentTheme.secondary + '20',
                              color: currentTheme.secondary,
                              border: `1px solid ${currentTheme.secondary}30`
                            }}
                          >
                            <FaEdit className="h-3 w-3" />
                          </button>

                          <button
                            onClick={() => handleDeleteClick(brand)}
                            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 hover:shadow-md"
                            style={{
                              backgroundColor: currentTheme.error + '20',
                              color: currentTheme.error,
                              border: `1px solid ${currentTheme.error}30`
                            }}
                          >
                            <MdDelete className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
            ) : (
              <div className="col-span-full text-center py-16">
                <div className="max-w-md mx-auto">
                  <div className="w-24 h-24 mx-auto mb-4 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: currentTheme.primary + '20' }}>
                    <FaBuilding className="h-12 w-12" style={{ color: currentTheme.primary }} />
                  </div>
                  <h3 className="text-xl font-semibold mb-2" style={{ color: currentTheme.text }}>
                    No brands found
                  </h3>
                  <p className="mb-6" style={{ color: currentTheme.textMuted }}>
                    {searchTerm ? 'No brands match your search criteria.' : 'Get started by creating your first brand.'}
                  </p>
                  {!searchTerm && isProfileVerified && (
                    <button
                      onClick={() => setIsModalOpen(true)}
                      className="px-6 py-3 rounded-xl font-semibold text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                      style={{ backgroundColor: currentTheme.primary }}
                    >
                      Create Your First Brand
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* DELETE CONFIRMATION MODAL */}
      {isDeleteModalOpen && brandToDelete && (
        <div className="fixed z-[105] inset-0 flex items-center justify-center p-4">
          <div style={{ backgroundColor: themes[themeName].colors.background + 'CC' }} className="fixed inset-0" onClick={handleCancelDelete}></div>
          <div className="rounded-2xl shadow-2xl w-full max-w-md relative transform animate-scale-in"
            style={{ backgroundColor: themes[themeName].colors.surface, color: themes[themeName].colors.text }}>

            {/* Modal Header */}
            <div className="px-6 py-5 border-b flex items-center gap-4" style={{ borderColor: currentTheme.border }}>
              <div className="h-12 w-12 rounded-full flex items-center justify-center shadow-lg"
                style={{ backgroundColor: currentTheme.error + '20' }}>
                <FaExclamationTriangle style={{ color: currentTheme.error }} className="h-6 w-6" />
              </div>
              <div>
                <h3 className="text-xl font-bold" style={{ color: currentTheme.error }}>Delete Brand</h3>
                <p className="text-sm" style={{ color: currentTheme.textMuted }}>This action cannot be undone</p>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              <div className="space-y-4">
                <p style={{ color: currentTheme.text }}>
                  Are you sure you want to delete <strong className="font-semibold">"{brandToDelete.title}"</strong>?
                </p>
                <div className="p-4 rounded-xl text-sm" style={{ backgroundColor: currentTheme.error + '10', color: currentTheme.error }}>
                  <div className="flex items-center gap-2 font-medium mb-1">
                    <FaExclamationTriangle className="h-4 w-4" />
                    Warning
                  </div>
                  <p>All brand data and associated documents will be permanently removed from the system.</p>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t flex justify-end gap-3" style={{ borderColor: currentTheme.border }}>
              <button
                onClick={handleCancelDelete}
                className="px-6 py-3 rounded-xl font-medium transition-all duration-200 hover:shadow-md"
                style={{
                  color: currentTheme.textSecondary,
                  backgroundColor: currentTheme.surface,
                  border: `1px solid ${currentTheme.border}`
                }}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={() => showConfirmation(
                  'Delete Brand',
                  `Are you sure you want to delete the brand "${brand.title}"? This action cannot be undone.`,
                  'Delete',
                  'Cancel',
                  () => handleDelete(brand.id)
                )}
                className="hover:opacity-80 transition flex items-center gap-1 text-sm" style={{ color: currentTheme.error }}
              >
                {isDeleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <FaTrash className="h-4 w-4" />
                    Delete Brand
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal for Adding Brand */}
      {isModalOpen && (
        <div className="fixed z-[101] inset-0 flex items-center justify-center p-4">
          <div style={{ backgroundColor: themes[themeName].colors.background + 'CC' }} className="fixed inset-0" onClick={() => setIsModalOpen(false)}></div>
          <div className="rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto relative transform animate-scale-in"
            style={{ backgroundColor: themes[themeName].colors.surface, color: themes[themeName].colors.text }}
            onClick={(e) => e.stopPropagation()}>

            {/* Modal Header */}
            <div className="px-6 py-5 border-b flex items-center justify-between" style={{ borderColor: currentTheme.border }}>
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-full flex items-center justify-center shadow-lg"
                  style={{ backgroundColor: currentTheme.primary + '20' }}>
                  <FaBuilding style={{ color: currentTheme.primary }} className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold" style={{ color: currentTheme.text }}>Create New Brand</h3>
                  <p className="text-sm" style={{ color: currentTheme.textMuted }}>Add a new brand to your portfolio</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  setNewBrand({
                    title: "",
                    description: "",
                    logo: null,
                    location: "",
                    district: "",
                    state: "",
                    pincode: ""
                  });
                }}
                className="h-10 w-10 rounded-full flex items-center justify-center transition-all duration-200 hover:shadow-md"
                style={{
                  backgroundColor: currentTheme.surface,
                  color: currentTheme.text,
                  border: `1px solid ${currentTheme.border}`
                }}
              >
                <FaTimes className="h-4 w-4" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              <div className="space-y-6">
                {/* Brand Name and Description */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold mb-3" style={{ color: themes[themeName].colors.textSecondary }}>
                      Brand Name *
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={newBrand.title}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-offset-2 transition-all duration-200"
                      placeholder="Enter brand name"
                      style={{
                        backgroundColor: themes[themeName].colors.surface,
                        color: themes[themeName].colors.text,
                        borderColor: themes[themeName].colors.border,
                        focusBorderColor: themes[themeName].colors.primary
                      }}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-3" style={{ color: themes[themeName].colors.textSecondary }}>
                      Pincode *
                    </label>
                    <input
                      type="text"
                      name="pincode"
                      value={newBrand.pincode}
                      onChange={handleChange}
                      maxLength={6}
                      className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-offset-2 transition-all duration-200"
                      placeholder="Enter 6-digit pincode"
                      style={{
                        backgroundColor: themes[themeName].colors.surface,
                        color: themes[themeName].colors.text,
                        borderColor: !isPinValid && newBrand.pincode.length === 6 ? themes[themeName].colors.error : themes[themeName].colors.border
                      }}
                    />
                    {!isPinValid && newBrand.pincode.length === 6 && (
                      <p className="text-sm mt-2 flex items-center gap-2" style={{ color: themes[themeName].colors.error }}>
                        <FaExclamationTriangle className="h-4 w-4" />
                        Invalid pincode
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-3" style={{ color: themes[themeName].colors.textSecondary }}>
                    Description *
                  </label>
                  <textarea
                    name="description"
                    value={newBrand.description}
                    onChange={handleChange}
                    rows={4}
                    className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-offset-2 transition-all duration-200 resize-none"
                    placeholder="Describe your brand, products, and services..."
                    style={{
                      backgroundColor: themes[themeName].colors.surface,
                      color: themes[themeName].colors.text,
                      borderColor: themes[themeName].colors.border
                    }}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-semibold mb-3" style={{ color: themes[themeName].colors.textSecondary }}>Location</label>
                    <input
                      type="text"
                      name="location"
                      value={newBrand.location}
                      readOnly
                      className="w-full px-4 py-3 border rounded-xl bg-opacity-50"
                      placeholder="Auto-filled from pincode"
                      style={{
                        backgroundColor: themes[themeName].colors.surface,
                        color: themes[themeName].colors.text,
                        borderColor: themes[themeName].colors.border
                      }}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-3" style={{ color: themes[themeName].colors.textSecondary }}>District</label>
                    <input
                      type="text"
                      name="district"
                      value={newBrand.district}
                      readOnly
                      className="w-full px-4 py-3 border rounded-xl bg-opacity-50"
                      placeholder="Auto-filled from pincode"
                      style={{
                        backgroundColor: themes[themeName].colors.surface,
                        color: themes[themeName].colors.text,
                        borderColor: themes[themeName].colors.border
                      }}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-3" style={{ color: themes[themeName].colors.textSecondary }}>State</label>
                    <input
                      type="text"
                      name="state"
                      value={newBrand.state}
                      readOnly
                      className="w-full px-4 py-3 border rounded-xl bg-opacity-50"
                      placeholder="Auto-filled from pincode"
                      style={{
                        backgroundColor: themes[themeName].colors.surface,
                        color: themes[themeName].colors.text,
                        borderColor: themes[themeName].colors.border
                      }}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-3" style={{ color: themes[themeName].colors.textSecondary }}>
                    Brand Logo *
                  </label>
                  <div className="mt-1 flex justify-center px-6 pt-8 pb-8 border-2 border-dashed rounded-2xl transition-all duration-200 hover:border-primary-500 hover:shadow-md"
                    style={{ borderColor: themes[themeName].colors.border }}>
                    <div className="space-y-4 text-center">
                      <div className="h-16 w-16 mx-auto rounded-full flex items-center justify-center"
                        style={{ backgroundColor: currentTheme.primary + '20' }}>
                        <FaFileUpload style={{ color: currentTheme.primary }} className="h-8 w-8" />
                      </div>
                      <div className="space-y-2">
                        <div className="flex text-sm justify-center gap-1" style={{ color: themes[themeName].colors.textSecondary }}>
                          <label className="relative cursor-pointer rounded-md font-semibold focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 transition-all duration-200"
                            style={{ color: themes[themeName].colors.primary }}>
                            <span className="hover:underline">Upload a file</span>
                            <input
                              type="file"
                              onChange={handleImageUpload}
                              accept="image/*"
                              className="sr-only"
                            />
                          </label>
                          <p className="pl-1">or drag and drop</p>
                        </div>
                        <p className="text-xs" style={{ color: themes[themeName].colors.textMuted }}>
                          PNG, JPG, GIF up to 10MB
                        </p>
                      </div>
                    </div>
                  </div>
                  {newBrand.logo && (
                    <div className="mt-4 p-4 rounded-xl border" style={{ borderColor: currentTheme.border }}>
                      <p className="text-sm font-medium mb-2" style={{ color: currentTheme.text }}>Preview:</p>
                      <img
                        src={newBrand.logo instanceof File ? URL.createObjectURL(newBrand.logo) : newBrand.logo}
                        alt="Preview"
                        className="h-32 w-full object-contain rounded-lg"
                        style={{ border: `1px solid ${themes[themeName].colors.border}` }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-5 border-t flex justify-end gap-4" style={{ backgroundColor: themes[themeName].colors.background, borderColor: themes[themeName].colors.border }}>
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-6 py-3 rounded-xl font-medium transition-all duration-200 hover:shadow-md"
                style={{
                  color: themes[themeName].colors.textSecondary,
                  backgroundColor: themes[themeName].colors.surface,
                  border: `1px solid ${themes[themeName].colors.border}`
                }}
                disabled={isUploading}
              >
                Cancel
              </button>
              <button
                onClick={handleAddBrand}
                disabled={isUploading}
                className="px-8 py-3 rounded-xl font-semibold flex items-center gap-3 transition-all duration-200 hover:shadow-lg transform hover:scale-105 disabled:transform-none disabled:hover:shadow-md"
                style={{
                  backgroundColor: themes[themeName].colors.primary,
                  color: themes[themeName].colors.surface,
                  opacity: isUploading ? 0.8 : 1
                }}
              >
                {isUploading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Creating Brand...
                  </>
                ) : (
                  <>
                    <FaPlus className="h-5 w-5" />
                    Create Brand
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* UPDATE BRAND MODAL - Similar structure to Add Brand Modal but with edit-specific styling */}
      {isEditModalOpen && editingBrand && (
        <div className="fixed z-[101] inset-0 flex items-center justify-center p-4">
          <div style={{ backgroundColor: themes[themeName].colors.background + 'CC' }} className="fixed inset-0"></div>
          <div className="rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto relative transform animate-scale-in"
            style={{ backgroundColor: themes[themeName].colors.surface, color: themes[themeName].colors.text }}>

            {/* Modal Header */}
            <div className="px-6 py-5 border-b flex items-center justify-between" style={{ borderColor: currentTheme.border }}>
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-full flex items-center justify-center shadow-lg"
                  style={{ backgroundColor: currentTheme.secondary + '20' }}>
                  <FaEdit style={{ color: currentTheme.secondary }} className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold" style={{ color: currentTheme.text }}>Update Brand</h3>
                  <p className="text-sm" style={{ color: currentTheme.textMuted }}>Edit your brand information</p>
                </div>
              </div>
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="h-10 w-10 rounded-full flex items-center justify-center transition-all duration-200 hover:shadow-md"
                style={{
                  backgroundColor: currentTheme.surface,
                  color: currentTheme.text,
                  border: `1px solid ${currentTheme.border}`
                }}
              >
                <FaTimes className="h-4 w-4" />
              </button>
            </div>

            {/* Modal Content - Similar to Add Brand but with editForm */}
            <div className="p-6">
              <div className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold mb-3" style={{ color: themes[themeName].colors.textSecondary }}>
                      Brand Name *
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={editForm.title}
                      onChange={handleEditChange}
                      className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-offset-2 transition-all duration-200"
                      placeholder="Enter brand name"
                      style={{
                        backgroundColor: themes[themeName].colors.surface,
                        color: themes[themeName].colors.text,
                        borderColor: themes[themeName].colors.border
                      }}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-3" style={{ color: themes[themeName].colors.textSecondary }}>
                      Pincode *
                    </label>
                    <input
                      type="text"
                      name="pincode"
                      value={editForm.pincode}
                      onChange={handleEditChange}
                      maxLength={6}
                      className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-offset-2 transition-all duration-200"
                      placeholder="Enter 6-digit pincode"
                      style={{
                        backgroundColor: themes[themeName].colors.surface,
                        color: themes[themeName].colors.text,
                        borderColor: !isEditPinValid && editForm.pincode.length === 6 ? themes[themeName].colors.error : themes[themeName].colors.border
                      }}
                    />
                    {!isEditPinValid && editForm.pincode.length === 6 && (
                      <p className="text-sm mt-2 flex items-center gap-2" style={{ color: themes[themeName].colors.error }}>
                        <FaExclamationTriangle className="h-4 w-4" />
                        Invalid pincode
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-3" style={{ color: themes[themeName].colors.textSecondary }}>
                    Description *
                  </label>
                  <textarea
                    name="description"
                    value={editForm.description}
                    onChange={handleEditChange}
                    rows={4}
                    className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-offset-2 transition-all duration-200 resize-none"
                    placeholder="Describe your brand, products, and services..."
                    style={{
                      backgroundColor: themes[themeName].colors.surface,
                      color: themes[themeName].colors.text,
                      borderColor: themes[themeName].colors.border
                    }}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-semibold mb-3" style={{ color: themes[themeName].colors.textSecondary }}>Location</label>
                    <input
                      type="text"
                      name="location"
                      value={editForm.location}
                      readOnly
                      className="w-full px-4 py-3 border rounded-xl bg-opacity-50"
                      placeholder="Auto-filled from pincode"
                      style={{
                        backgroundColor: themes[themeName].colors.surface,
                        color: themes[themeName].colors.text,
                        borderColor: themes[themeName].colors.border
                      }}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-3" style={{ color: themes[themeName].colors.textSecondary }}>District</label>
                    <input
                      type="text"
                      name="district"
                      value={editForm.district}
                      readOnly
                      className="w-full px-4 py-3 border rounded-xl bg-opacity-50"
                      placeholder="Auto-filled from pincode"
                      style={{
                        backgroundColor: themes[themeName].colors.surface,
                        color: themes[themeName].colors.text,
                        borderColor: themes[themeName].colors.border
                      }}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-3" style={{ color: themes[themeName].colors.textSecondary }}>State</label>
                    <input
                      type="text"
                      name="state"
                      value={editForm.state}
                      readOnly
                      className="w-full px-4 py-3 border rounded-xl bg-opacity-50"
                      placeholder="Auto-filled from pincode"
                      style={{
                        backgroundColor: themes[themeName].colors.surface,
                        color: themes[themeName].colors.text,
                        borderColor: themes[themeName].colors.border
                      }}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-3" style={{ color: themes[themeName].colors.textSecondary }}>
                    Brand Logo
                  </label>
                  <div className="mt-1 flex justify-center px-6 pt-8 pb-8 border-2 border-dashed rounded-2xl transition-all duration-200 hover:border-primary-500 hover:shadow-md"
                    style={{ borderColor: themes[themeName].colors.border }}>
                    <div className="space-y-4 text-center">
                      <div className="h-16 w-16 mx-auto rounded-full flex items-center justify-center"
                        style={{ backgroundColor: currentTheme.secondary + '20' }}>
                        <FaFileUpload style={{ color: currentTheme.secondary }} className="h-8 w-8" />
                      </div>
                      <div className="space-y-2">
                        <div className="flex text-sm justify-center gap-1" style={{ color: themes[themeName].colors.textSecondary }}>
                          <label className="relative cursor-pointer rounded-md font-semibold focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 transition-all duration-200"
                            style={{ color: themes[themeName].colors.secondary }}>
                            <span className="hover:underline">Upload new logo</span>
                            <input
                              type="file"
                              onChange={handleEditImageUpload}
                              accept="image/*"
                              className="sr-only"
                            />
                          </label>
                          <p className="pl-1">or drag and drop</p>
                        </div>
                        <p className="text-xs" style={{ color: themes[themeName].colors.textMuted }}>
                          PNG, JPG, GIF up to 10MB
                        </p>
                      </div>
                    </div>
                  </div>
                  {editForm.logo && (
                    <div className="mt-4 p-4 rounded-xl border" style={{ borderColor: currentTheme.border }}>
                      <p className="text-sm font-medium mb-2" style={{ color: currentTheme.text }}>Current Logo:</p>
                      <img
                        src={editForm.logo instanceof File ? URL.createObjectURL(editForm.logo) : editForm.logo}
                        alt="Preview"
                        className="h-32 w-full object-contain rounded-lg"
                        style={{ border: `1px solid ${themes[themeName].colors.border}` }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-5 border-t flex justify-end gap-4" style={{ backgroundColor: currentTheme.background, borderColor: currentTheme.border }}>
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="px-6 py-3 rounded-xl font-medium transition-all duration-200 hover:shadow-md"
                style={{
                  color: currentTheme.textSecondary,
                  backgroundColor: currentTheme.surface,
                  border: `1px solid ${currentTheme.border}`
                }}
                disabled={isUploading}
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateBrand}
                disabled={isUploading}
                className="px-8 py-3 rounded-xl font-semibold flex items-center gap-3 transition-all duration-200 hover:shadow-lg transform hover:scale-105 disabled:transform-none disabled:hover:shadow-md"
                style={{
                  backgroundColor: currentTheme.secondary,
                  color: currentTheme.surface,
                  opacity: isUploading ? 0.8 : 1
                }}
              >
                {isUploading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    <FaEdit className="h-5 w-5" />
                    Update Brand
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Brand Modal - Keep existing but could be enhanced similarly */}
      {viewBrand && (
        <div className="fixed z-[100] inset-0 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="rounded-2xl shadow-2xl w-full max-w-4xl mx-auto max-h-[90vh] flex flex-col transform animate-scale-in" style={{ backgroundColor: currentTheme.card }}>
            {/* Header */}
            <div className="px-6 py-5 border-b flex-shrink-0 flex items-center justify-between" style={{ borderColor: currentTheme.border }}>
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-full flex items-center justify-center shadow-lg"
                  style={{ backgroundColor: currentTheme.primary + '20' }}>
                  <FaBuilding style={{ color: currentTheme.primary }} className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold" style={{ color: currentTheme.text }}>Brand Details</h3>
                  <p className="text-sm" style={{ color: currentTheme.textMuted }}>Complete brand information</p>
                </div>
              </div>
              <button
                onClick={() => setViewBrand(null)}
                className="h-10 w-10 rounded-full flex items-center justify-center transition-all duration-200 hover:shadow-md"
                style={{
                  backgroundColor: currentTheme.surface,
                  color: currentTheme.text,
                  border: `1px solid ${currentTheme.border}`
                }}
              >
                <FaTimes className="h-4 w-4" />
              </button>
            </div>

            {/* Content - Enhanced version */}
            <div className="p-6 flex-1 overflow-y-auto">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left Column - Image & Actions */}
                <div className="lg:col-span-1">
                  <div className="rounded-2xl p-6 shadow-lg border" style={{ backgroundColor: currentTheme.surface, borderColor: currentTheme.border }}>
                    <img
                      src={viewBrand.logo}
                      alt={viewBrand.title}
                      className="w-full h-48 object-cover rounded-xl mb-6"
                    />
                    <div className="space-y-3">
                      <button
                        className="w-full px-4 py-3 rounded-xl transition-all duration-200 hover:shadow-lg flex items-center justify-center gap-3 font-medium"
                        style={{ backgroundColor: currentTheme.primary, color: currentTheme.surface }}
                        onClick={() => setIsDocumentModalOpen(true)}
                      >
                        <FaFileUpload className="h-5 w-5" />
                        Upload Documents
                      </button>
                      <button
                        className="w-full px-4 py-3 rounded-xl transition-all duration-200 hover:shadow-lg flex items-center justify-center gap-3 font-medium border"
                        style={{ backgroundColor: currentTheme.card, color: currentTheme.primary, borderColor: currentTheme.border }}
                        onClick={() => {
                          if (viewBrand?.id) {
                            fetchBrandDocuments(viewBrand.id);
                            setIsViewDocumentsModalOpen(true);
                          }
                        }}
                      >
                        <FaFile className="h-5 w-5" />
                        View Documents
                      </button>
                    </div>
                  </div>
                </div>

                {/* Right Column - Details */}
                <div className="lg:col-span-2">
                  <div className="space-y-6">
                    {/* Main Info Card */}
                    <div className="rounded-2xl p-6 shadow-lg border" style={{ backgroundColor: currentTheme.surface, borderColor: currentTheme.border }}>
                      <h4 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: currentTheme.primary }}>
                        <FaBuilding className="h-5 w-5" />
                        Brand Information
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium mb-2 block" style={{ color: currentTheme.textMuted }}>Brand Name</label>
                          <p className="text-lg font-semibold" style={{ color: currentTheme.text }}>{viewBrand?.title}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium mb-2 block" style={{ color: currentTheme.textMuted }}>Location</label>
                          <p className="text-lg" style={{ color: currentTheme.text }}>{viewBrand.location}</p>
                        </div>
                      </div>
                    </div>

                    {/* Description Card */}
                    <div className="rounded-2xl p-6 shadow-lg border" style={{ backgroundColor: currentTheme.surface, borderColor: currentTheme.border }}>
                      <h4 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: currentTheme.primary }}>
                        <FaFile className="h-5 w-5" />
                        Description
                      </h4>
                      <p className="leading-relaxed" style={{ color: currentTheme.text }}>{viewBrand.description}</p>
                    </div>

                    {/* Additional Info Card */}
                    <div className="rounded-2xl p-6 shadow-lg border" style={{ backgroundColor: currentTheme.surface, borderColor: currentTheme.border }}>
                      <h4 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: currentTheme.primary }}>
                        <FaIdCard className="h-5 w-5" />
                        Additional Information
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex justify-between items-center py-3 border-b" style={{ borderColor: currentTheme.border }}>
                          <span style={{ color: currentTheme.textSecondary }}>District</span>
                          <span className="font-semibold" style={{ color: currentTheme.text }}>{viewBrand.district || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between items-center py-3 border-b" style={{ borderColor: currentTheme.border }}>
                          <span style={{ color: currentTheme.textSecondary }}>State</span>
                          <span className="font-semibold" style={{ color: currentTheme.text }}>{viewBrand.state || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between items-center py-3 border-b" style={{ borderColor: currentTheme.border }}>
                          <span style={{ color: currentTheme.textSecondary }}>Pincode</span>
                          <span className="font-semibold" style={{ color: currentTheme.text }}>{viewBrand.pincode || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between items-center py-3 border-b" style={{ borderColor: currentTheme.border }}>
                          <span style={{ color: currentTheme.textSecondary }}>Created On</span>
                          <span className="font-semibold flex items-center gap-2" style={{ color: currentTheme.text }}>
                            <FaCalendar className="h-4 w-4" />
                            {new Date(viewBrand.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t flex justify-end flex-shrink-0" style={{ backgroundColor: currentTheme.surface, borderColor: currentTheme.border }}>
              <button
                onClick={() => setViewBrand(null)}
                className="px-6 py-3 rounded-xl font-medium transition-all duration-200 hover:shadow-md"
                style={{ backgroundColor: currentTheme.card, color: currentTheme.text, border: `1px solid ${currentTheme.border}` }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document Upload Modal - Enhanced */}
      {isDocumentModalOpen && (
        <div className="fixed z-[110] inset-0 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden transform animate-scale-in">
            <div className="px-6 py-5 border-b flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-full flex items-center justify-center shadow-lg"
                  style={{ backgroundColor: currentTheme.primary + '20' }}>
                  <FaFileUpload style={{ color: currentTheme.primary }} className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">Upload Documents</h3>
                  <p className="text-sm text-gray-600">Add documents to your brand</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setIsDocumentModalOpen(false);
                  setDocuments([]);
                }}
                className="h-10 w-10 rounded-full flex items-center justify-center transition-all duration-200 hover:shadow-md border border-gray-300"
              >
                <FaTimes className="h-4 w-4 text-gray-600" />
              </button>
            </div>

            <div className="p-6">
              <div className="space-y-6">
                {/* Document Name Input */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Document Name
                  </label>
                  <input
                    type="text"
                    value={documentName}
                    onChange={(e) => setDocumentName(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                    placeholder="Enter document name"
                  />
                </div>

                {/* File Upload */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Select File
                  </label>
                  <input
                    type="file"
                    onChange={handleDocumentUpload}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                    disabled={!documentName.trim()}
                  />
                </div>

                {/* Documents List */}
                <div className="mt-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Selected Documents</h4>
                  <div className="space-y-3 max-h-[200px] overflow-y-auto">
                    {documents.map((doc, index) => (
                      <div key={index} className="space-y-2">
                        <div className="flex items-center justify-between p-4 rounded-xl border border-gray-200 bg-gray-50">
                          <div className="flex items-center gap-3">
                            <FaFile className="h-5 w-5 text-blue-500" />
                            <span className="text-sm font-medium text-gray-800">{doc.name}</span>
                          </div>
                          {!isUploading && (
                            <button
                              onClick={() => removeDocument(index)}
                              className="text-red-500 hover:text-red-700 transition-colors duration-200 p-2 rounded-lg hover:bg-red-50"
                            >
                              <FaTrash className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                        {isUploading && uploadProgress[index] && (
                          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                            <div
                              className="h-full transition-all duration-300 ease-in-out rounded-full"
                              style={{ width: `${uploadProgress[index].progress}%`, backgroundColor: currentTheme.primary }}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                    {documents.length === 0 && (
                      <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-xl">
                        <FaFile className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-gray-500">No documents added yet</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 py-5 border-t bg-gray-50 flex justify-end gap-4">
              <button
                onClick={() => {
                  setIsDocumentModalOpen(false);
                  setDocuments([]);
                  setUploadProgress([]);
                }}
                className="px-6 py-3 bg-white border border-gray-300 rounded-xl font-medium text-gray-700 transition-all duration-200 hover:shadow-md"
                disabled={isUploading}
              >
                Cancel
              </button>
              <button
                onClick={handleDocumentSubmission}
                disabled={documents.length === 0 || isUploading}
                className="px-8 py-3 text-white rounded-xl font-semibold transition-all duration-200 hover:shadow-lg transform hover:scale-105 disabled:transform-none disabled:hover:shadow-md disabled:opacity-50 flex items-center gap-3"
                style={{ backgroundColor: currentTheme.primary }}
              >
                {isUploading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Uploading...
                  </>
                ) : (
                  'Upload Documents'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Documents Modal - Enhanced */}
      {isViewDocumentsModalOpen && (
        <div className="fixed z-[120] inset-0 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden transform animate-scale-in">
            <div className="px-6 py-5 border-b flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-full flex items-center justify-center shadow-lg"
                  style={{ backgroundColor: currentTheme.primary + '20' }}>
                  <FaFile style={{ color: currentTheme.primary }} className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">Brand Documents</h3>
                  <p className="text-sm text-gray-600">Manage your brand documents</p>
                </div>
              </div>
              <button
                onClick={() => setIsViewDocumentsModalOpen(false)}
                className="h-10 w-10 rounded-full flex items-center justify-center transition-all duration-200 hover:shadow-md border border-gray-300"
              >
                <FaTimes className="h-4 w-4 text-gray-600" />
              </button>
            </div>

            <div className="p-6">
              {loadingDocuments ? (
                <div className="flex justify-center items-center py-16">
                  <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-gray-600">Loading documents...</p>
                  </div>
                </div>
              ) : brandDocuments.length > 0 ? (
                <div className="space-y-4">
                  {brandDocuments.map((doc) => (
                    <div key={doc.id} className="bg-gray-50 rounded-xl p-4 flex items-center justify-between border border-gray-200">
                      <div className="flex items-center gap-4">
                        <div className="h-12 w-12 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: currentTheme.primary + '20' }}>
                          <FaFile style={{ color: currentTheme.primary }} className="h-6 w-6" />
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-gray-800">{doc.name}</h4>
                          <p className="text-xs text-gray-500 flex items-center gap-2">
                            <FaCalendar className="h-3 w-3" />
                            Uploaded on {new Date(doc.uploaded_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => window.open(process.env.REACT_APP_PF + doc.file, '_blank')}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 hover:shadow-md"
                        style={{
                          backgroundColor: currentTheme.primary,
                          color: 'white'
                        }}
                        title="View Document"
                      >
                        <FaEye className="h-4 w-4" />
                        View
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <FaFile className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">No documents found</h3>
                  <p className="text-gray-500">Upload documents to get started</p>
                </div>
              )}
            </div>

            <div className="px-6 py-5 border-t bg-gray-50 flex justify-end">
              <button
                onClick={() => setIsViewDocumentsModalOpen(false)}
                className="px-6 py-3 text-gray-700 bg-white border border-gray-300 rounded-xl font-medium transition-all duration-200 hover:shadow-md"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add CSS for animations */}
      <style jsx>{`
        @keyframes scale-in {
          from {
            transform: scale(0.9);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
        .animate-scale-in {
          animation: scale-in 0.2s ease-out;
        }
      `}</style>
    </div>
  );
};

export default Brands;